import React, { useState, useEffect, useRef } from 'react';
import { Page, Dashboard, ReflexThreeHorizontalContainer } from '../../templates';
import { Content } from '../../organisms';
import CadDataFilter from './Filter';
import { CadDataSub } from './CadDataSub';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { FlexBoxVertical, ToggleSwitch, FlexBoxHorizontal, BackdropPanel } from '../../atoms';
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import Menu from '../../../utils/menu';
import { LanguageButton } from '../../molecules';
import i18n from '../../../utils/i18n';
import { useTranslation } from 'react-i18next';
import { translateText } from '../../../utils/translateText';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import LinearProgress from '@mui/material/LinearProgress';
import palette from '../../../utils/colorPalette';
import { useLocation } from 'react-router-dom';
import { useUser } from '../../../contexts/UserContext';
import Unauthorized from '../../auth/Unauthorized';
import { drawCadCard, makeZip } from '../../../utils/cadCardImage';

function CadData() {
  const { t } = useTranslation();
  const language = i18n.language;
  const location = useLocation();
  const ismatrixBoardPage = location.pathname === '/interstellar/matrix-board';
  const [isLoading, setIsLoading] = useState(false);
  const [snowflakeData, setSnowflakeData] = useState([]);
  const [filteredData, setFilteredData] = useState([]); // 필터링된 데이터 상태 관리
  const [selectedRow, setSelectedRow] = useState(null);
  const [lifeStyleList, setLifeStyleList] = useState([]); // distinct LifeStyle 값들을 저장하는 상태
  const [tableData, setTableData] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [filterOptions, setFilterOptions] = useState(null);
  const [viewMode, setViewMode] = useState('List');
  const [translatedColumns, setTranslatedColumns] = useState(columns(language));
  const [downloadProgress, setDownloadProgress] = useState(false);
  const [imgForDownload, setImgForDownload] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false); // 다운로드 상태 관리
  const [abortController, setAbortController] = useState(null); // AbortController 상태 추가
  const [cardProgress, setCardProgress] = useState(null); // 카드형 다운로드 진행 { done, total }
  const cardCancelRef = useRef(false);
  const { user, permissions, hasPermission, loading: userLoading } = useUser();

  useEffect(() => {
    setIsLoading(true);
    setFilterOptions(null);
    // 페이지 로드 시 Snowflake 데이터 가져오기
    fetch('/api/cad-data', {
      method: 'POST',  // POST 메소드로 변경
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ brd_cd: 'M' })  // selectedBrand 값을 전송
    })
      .then(response => response.json())
      .then(data => {
        const sortedData = data.sort((a, b) => a.row_num - b.row_num);
        setSnowflakeData(sortedData);
        setFilterOptions({
          designer: createOptions(sortedData, 'designer'),
          item: createOptions(sortedData, 'item'),
          parentPrdtKind: createOptions(sortedData, 'parent_prdt_kind'),
          prdtKind: createOptions(sortedData, 'prdt_kind'),
          sesn: createOptions(sortedData, 'sesn'),
          fabric: createOptions(sortedData, 'fabric'),
          lifeStyle: createOptions(sortedData, 'life_style'),
          vendor: createOptions(sortedData, 'vendor'),
          graphic: createOptions(sortedData.flatMap((row) => String(row.graphic).split(',').map((code) => ({ graphic: code }))), 'graphic')
        });
        setIsLoading(false);
      })
      .catch(error => {
        console.error('Error fetching Snowflake data:', error);
        setIsLoading(false);
      });
    setSelectedRow(null);
  }, []);


  // lifeStyleList가 변경되었을 때 rowSelectionModel을 설정
  useEffect(() => {
    if (lifeStyleList.length > 0) {
      setRowSelectionModel(lifeStyleList.map(item => item.lifeStyle));
    }
  }, [lifeStyleList]);


  // 좌측 라이프스타일 목록은 lifestyle1_nm(라이프스타일_메인) 기준이다. 서버 응답 키는 'domain'이고,
  // 'life_style'(imc_nm)은 상단 IMC 필터가 쓴다
  useEffect(() => {
    if (filteredData.length > 0) {
      // 각 LifeStyle의 개수를 계산
      const lifeStyleCounts = filteredData.reduce((acc, data) => {
        const lifeStyle = data['domain'];
        acc[lifeStyle] = (acc[lifeStyle] || 0) + 1;
        return acc;
      }, {});

      // 중복 제거한 LifeStyle 값과 개수를 객체 형식으로 변환
      const uniqueLifeStyles = Array.from(new Set(filteredData.map((data) => data['domain'])))
        .map((lifeStyle) => ({
          lifeStyle: `${lifeStyle} (${lifeStyleCounts[lifeStyle]})`,
        }));

      setLifeStyleList(uniqueLifeStyles);
    } else {
      setLifeStyleList([]);
    }
  }, [filteredData]);

  useEffect(() => {
    const lifeStyleOnly = rowSelectionModel.map((lifeStyle) => {
      const cleanedLifeStyle = lifeStyle.replace(/\s*\(\d+\)$/, ''); // (숫자) 제거
      return cleanedLifeStyle
    });

    // filteredData에서 선택된 domain 값들에 맞는 데이터 필터링
    const newTableData = filteredData.filter((data) =>
      lifeStyleOnly.includes(data['domain'])
    );

    setTableData(newTableData); // 필터링된 데이터를 설정
  }, [rowSelectionModel, filteredData]); // `filteredData`와 `rowSelectionModel`을 의존성 배열에 추가


  // 상세 패널에서 그래픽 코드를 저장하면 그리드 행에도 바로 반영
  const handleGraphicSaved = (row, codes) => {
    const graphic = codes.length > 0 ? codes.join(',') : '-';
    const apply = (list) => list.map((item) => (item.part_cd === row.part_cd && item.sesn === row.sesn ? { ...item, graphic } : item));
    setSnowflakeData(apply);
    setFilteredData(apply);
    setSelectedRow((prev) => (prev ? { ...prev, graphic } : prev));
  };

  const filterData = (options) => {
    const hasSearchValue = options.searchValue && options.searchValue.trim() !== '';

    // 비교 키 정의
    const comparisonKeys = {
      parentPrdtKind: 'parent_prdt_kind',
      prdtKind: 'prdt_kind',
      item: 'item',
      sesn: 'sesn',
      designer: 'designer',
      fabric: 'fabric',
      lifeStyle: 'life_style',
      vendor: 'vendor',
      graphic: 'graphic'
    };

    // 필터링 로직
    const filteredData = snowflakeData.filter((item) => {
      const searchValueMatch = hasSearchValue
        ? ['part_cd', 'description', 'color', 'description_eng'].some((field) =>
          item[field]?.toString().toLowerCase().includes(options.searchValue.toLowerCase())
        )
        : true;

      const comparisonMatch = Object.keys(comparisonKeys).every((key) => {
        if (options[key].length === filterOptions[key].length) return true;
        // 그래픽 코드는 한 품번에 여럿이 쉼표로 붙어 있어 하나라도 맞으면 통과. 다른 값은 쉼표가 이름의 일부일 수 있어 쪼개지 않는다 (예: 협력사 'Co.,LTD')
        const value = String(item[comparisonKeys[key]]).toUpperCase();
        const values = key === 'graphic' ? value.split(',') : [value];
        return options[key].some((option) => values.includes(option.value.toUpperCase()));
      });



      return searchValueMatch && comparisonMatch;
    });
    setFilteredData(filteredData);
    setTimeout(() => { setIsLoading(false) }, 500);
  };

  const handleImageSrcUpdate = (newImgSrc) => {
    setTableData((prevTableData) =>
      prevTableData.map((row) =>
        row.part_cd === selectedRow.part_cd
          ? { ...row, img: newImgSrc } // 해당 row의 img 값을 업데이트
          : row
      )
    );
  };
  useEffect(() => {
    const checkImages = async () => {
      const newValidImages = new Set();

      await Promise.all(
        tableData.map((item) =>
          new Promise((resolve) => {
            const img = new Image();
            img.src = item.img;

            img.onload = () => {
              newValidImages.add(item.img);
              resolve();
            };

            img.onerror = () => {
              // 원본 실패 시 fallback URL 체크
              const fallbackUrl = `https://static-dashff.fnf.co.kr/interstellar/cad_img/${item.brd_cd}/${item.sesn}/${item.part_cd}.jpg`;
              const fallbackImg = new Image();
              fallbackImg.src = fallbackUrl;
              fallbackImg.onload = () => {
                newValidImages.add(fallbackUrl);
                resolve();
              };
              fallbackImg.onerror = () => {
                resolve();
              };
            };
          })
        )
      );

      setImgForDownload(newValidImages);
    };

    checkImages();
  }, [tableData]);

  const imageDownload = async () => {
    const imagesData = tableData
      .map((item) => {
        const fallbackUrl = `https://static-dashff.fnf.co.kr/interstellar/cad_img/${item.brd_cd}/${item.sesn}/${item.part_cd}.jpg`;
        const url = imgForDownload.has(item.img) ? item.img
          : imgForDownload.has(fallbackUrl) ? fallbackUrl
            : null;
        return url ? { url, fileName: `${item.part_cd}.jpg` } : null;
      })
      .filter(Boolean);

    if (imagesData.length === 0) {
      alert('다운로드할 이미지가 없습니다.');
      return;
    }

    const controller = new AbortController(); // 새로운 AbortController 생성
    setAbortController(controller); // 상태에 저장

    try {
      setDownloadProgress(true);
      setIsDownloading(true); // 다운로드 시작
      const response = await fetch('/api/download-images', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          images: imagesData
        }),
        signal: controller.signal // 요청에 signal 추가
      });

      if (!response.ok) {
        throw new Error('이미지 다운로드 실패');
      }

      const blob = await response.blob();
      const downloadLink = document.createElement('a');
      downloadLink.href = URL.createObjectURL(blob);
      downloadLink.download = 'cad_images.zip';
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(downloadLink.href);
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('다운로드가 취소되었습니다.'); // 다운로드 취소 로그
      } else {
        console.error('이미지 다운로드 중 오류 발생:', error);
        alert('이미지 다운로드 중 오류가 발생했습니다.');
      }
    } finally {
      setDownloadProgress(false);
      setIsDownloading(false); // 다운로드 종료
      setAbortController(null); // AbortController 상태 초기화
    }
  };

  // 조회된 카드를 A4 가로 PNG로 한 장씩 그려 zip으로 받는다
  const cardDownload = async () => {
    if (tableData.length === 0) {
      alert('다운로드할 카드가 없습니다.');
      return;
    }
    // 카드 PNG를 전부 브라우저 메모리에 모았다가 묶는다. 4GB를 넘는 zip은 makeZip이 만들지 못한다
    // 999장 시험: 한 장 평균 0.55MB, 최대 1.6MB, 크롬 메모리 최고 1.1GB, 85초
    // ponytail: 1,000은 그 시험을 통과한 값이다. 늘리려면 메모리가 적은 PC에서 먼저 확인한다
    if (tableData.length > CARD_DOWNLOAD_LIMIT) {
      alert(`카드형 이미지는 한 번에 ${CARD_DOWNLOAD_LIMIT.toLocaleString()}장까지 받을 수 있습니다.\n필터로 범위를 줄여 주세요. (현재 ${tableData.length.toLocaleString()}장)`);
      return;
    }

    cardCancelRef.current = false;
    const files = [];
    try {
      for (const [index, item] of tableData.entries()) {
        if (cardCancelRef.current) return;
        setCardProgress({ done: index, total: tableData.length });
        files.push({ name: `${item.sesn}_${item.part_cd}.png`, blob: await drawCadCard(item, language) });
      }

      const zip = await makeZip(files);
      const downloadLink = document.createElement('a');
      downloadLink.href = URL.createObjectURL(zip);
      downloadLink.download = 'cad_cards.zip';
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(downloadLink.href);
    } catch (error) {
      console.error('카드형 이미지 다운로드 중 오류 발생:', error);
      alert('카드형 이미지 다운로드 중 오류가 발생했습니다.');
    } finally {
      setCardProgress(null);
    }
  };

  useEffect(() => {
    setTranslatedColumns(columns(language).map((column) => {
      return {
        ...column,
        headerName: translateText({
          text: column.headerName,
          globalLanguage: language,
        }),
      };
    }));
  }, [language]);


  return (
    <Page>
      {isLoading ? <BackdropPanel /> : null}
      { !hasPermission('cadDashboard', 'view') ? <Unauthorized title="Cad Dashboard" brd_cd="M" language={language} /> : 
      <>
      <Menu
        targetPaths={['/interstellar/M/caddata', '/interstellar/M/caddata/pdf', '/interstellar/M/graphic']}
        menuItems={[
          { path: '/interstellar/M/caddata', label: 'CAD Dashboard' },
          { path: '/interstellar/M/caddata/pdf', label: 'CAD PDF View' },
          { path: '/interstellar/M/graphic', label: 'Graphic Master' }
        ]}
      />
      {!ismatrixBoardPage && <LanguageButton />}
      <Dashboard>
        {!ismatrixBoardPage ? <h1 style={{ color: '#353339', margin: '30px' }}>CAD Dashboard</h1> : <div style={{ height: '10px' }}></div>}
        {filterOptions ? <CadDataFilter data={filterOptions} onSearch={filterData} /> : <></>}
        <div style={{ alignSelf: 'flex-end', margin: '0 30px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{
            display: 'flex',
            border: '1px solid #e0e0e0',
            borderRadius: '4px',
            overflow: 'hidden',
            backgroundColor: '#ffffff',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          }}>
            <Tooltip
              title={
                <div style={{ whiteSpace: 'pre-line' }}>
                  {`${translateText({
                    text: "이미지 일괄 다운로드 (zip)\n이미지 개수와 네트워크 환경에 따라 다운로드 시간이 달라질 수 있으며,\n많을 경우 1분 이상 걸릴 수 있습니다.\n\n현재 조회된 이미지 수: ",
                    globalLanguage: language,
                  })} ${imgForDownload.size}`}
                </div>
              }
              PopperProps={{
                sx: {
                  '& .MuiTooltip-tooltip': {
                    maxWidth: 500, // Tooltip의 최대 넓이를 500px로 설정
                  },
                },
              }}
            >
              <div style={{ position: 'relative', width: '130px' }}>
                <IconButton
                  onClick={() => {
                    if (isDownloading) {
                      // 다운로드 중일 때 클릭 시 다운로드 중단
                      abortController.abort(); // 다운로드 요청 취소
                      setDownloadProgress(false);
                      setIsDownloading(false);
                    } else {
                      imageDownload();
                    }
                  }}
                  sx={downloadButtonSx}
                >
                  <SystemUpdateAltIcon sx={{ fontSize: 16, color: '#666', marginRight: '5px' }} />
                  {isDownloading ? (
                    <>
                      <span style={{ margin: '0 5px 0 0' }}>{translateText({ text: '다운로드 중...', globalLanguage: language })}</span>
                      <span style={{ color: 'rgb(255, 109, 135)', fontWeight: 'bold' }}>X</span>
                    </>
                  ) : (
                    translateText({ text: '이미지 다운로드', globalLanguage: language })
                  )}
                </IconButton>
                {downloadProgress && <LinearProgress sx={downloadProgressSx} />}
              </div>
            </Tooltip>
            <Tooltip
              title={
                <div style={{ whiteSpace: 'pre-line' }}>
                  {`${translateText({
                    text: "카드형 이미지 일괄 다운로드 (zip)\n조회된 카드를 A4 가로 크기 이미지(PNG)로 한 장씩 만듭니다.\n카드가 많으면 시간이 오래 걸릴 수 있습니다.\n\n현재 조회된 카드 수: ",
                    globalLanguage: language,
                  })} ${tableData.length}`}
                </div>
              }
              PopperProps={{ sx: { '& .MuiTooltip-tooltip': { maxWidth: 500 } } }}
            >
              <div style={{ position: 'relative', width: '170px', borderLeft: '1px solid #e0e0e0' }}>
                <IconButton
                  onClick={() => {
                    if (cardProgress) {
                      cardCancelRef.current = true; // 진행 중에 누르면 중단
                    } else {
                      cardDownload();
                    }
                  }}
                  sx={downloadButtonSx}
                >
                  <SystemUpdateAltIcon sx={{ fontSize: 16, color: '#666', marginRight: '5px' }} />
                  {cardProgress ? (
                    <>
                      <span style={{ margin: '0 5px 0 0' }}>{`${cardProgress.done}/${cardProgress.total}`}</span>
                      <span style={{ color: 'rgb(255, 109, 135)', fontWeight: 'bold' }}>X</span>
                    </>
                  ) : (
                    translateText({ text: '카드형 이미지 다운로드', globalLanguage: language })
                  )}
                </IconButton>
                {cardProgress && (
                  <LinearProgress
                    variant="determinate"
                    value={(cardProgress.done / cardProgress.total) * 100}
                    sx={downloadProgressSx}
                  />
                )}
              </div>
            </Tooltip>
          </div>
          <ToggleSwitch onToggle={setViewMode} />
        </div>
        <Content
          style={{
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <ReflexThreeHorizontalContainer
            Left={
              <DataGridPremium
                sx={style}
                columns={[language === 'ko' ? { field: 'lifeStyle', headerName: '라이프스타일', width: 200 } : { field: 'lifeStyle', headerName: 'LifeStyle', width: 200 }]}
                rows={lifeStyleList}
                getRowId={(row) => row['lifeStyle']}
                hideFooter
                checkboxSelection
                rowHeight={40}
                onRowSelectionModelChange={(newRowSelectionModel) => {
                  setRowSelectionModel(newRowSelectionModel);
                }}
                rowSelectionModel={rowSelectionModel}
              />
            }
            Middle={
              viewMode === 'List' ?
                <DataGridPremium
                  sx={style}
                  columns={translatedColumns}
                  rows={tableData}
                  getRowId={(row) => row['sesn'] + '_' + row['part_cd']}
                  onRowClick={(params) => setSelectedRow(params.row)}
                  hideFooter
                  rowHeight={40}
                /> :
                <Grid tableData={tableData} setSelectedRow={setSelectedRow} language={language} />
            }
            Right={
              <CadDataSub data={selectedRow} changeImg={handleImageSrcUpdate} onGraphicSaved={handleGraphicSaved} />
            }
            ratio={{ left: 0.12, middle: 0.62 }}
          />
        </Content>
      </Dashboard>
      </>
      }
    </Page>
  );
}

export default CadData;


const CARD_DOWNLOAD_LIMIT = 1000;

const downloadButtonSx = {
  padding: '4px',
  width: '100%',
  height: '27px',
  borderRadius: '0',
  '&:hover': {
    backgroundColor: '#f5f5f5',
  },
  transition: 'background-color 0.1s ease',
  fontSize: '12px',
};

const downloadProgressSx = {
  position: 'absolute',
  bottom: 0,
  left: 0,
  width: '100%',
  height: '2px',
  backgroundColor: 'rgba(97, 97, 230, 0.2)',
  '& .MuiLinearProgress-bar': {
    backgroundColor: palette.button.primary
  }
};


const columns = (language) => [
  { field: 'part_cd', headerName: '품번', width: 110 },
  {
    field: 'img',
    headerName: '이미지',
    width: 70,
    renderCell: (props) =>
    (
      <div style={{ position: 'relative', width: '2vw', height: '3.5vh' }}>
        <img
          src={props.value}
          onError={(e) => {
            if (!e.target.dataset.fallback) {
              e.target.dataset.fallback = '1';
              e.target.src = `https://static-dashff.fnf.co.kr/interstellar/cad_img/${props.row.brd_cd}/${props.row.sesn}/${props.row.part_cd}.jpg?timestamp=${Date.now()}`;
            } else {
              e.target.src = 'https://img.icons8.com/?size=100&id=IuQpbmYpl7VN&format=png&color=dadee0';
            }
          }}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            margin: '3px 0 3px 0',
          }}
        />
      </div>
    )
  },
  {
    field: language === 'ko' ? 'description' : 'description_eng',
    headerName: '제품명',
    width: 300
  },
  { field: 'parent_prdt_kind', headerName: '대분류', width: 90 },
  { field: 'prdt_kind', headerName: '중분류', width: 90 },
  { field: 'item', headerName: '아이템', width: 90 },
  { field: 'fabric', headerName: '패브릭', width: 150 },
  { field: 'domain', headerName: '도메인', width: 100 },
  { field: 'set_up_style_no', headerName: '셋업스타일넘버', width: 120 },
  { field: 'fit', headerName: '핏', width: 140 },
  { field: 'tag', headerName: '택가', width: 100 },
  { field: 'life_style', headerName: '라이프스타일', width: 120 },
  { field: 'level', headerName: '레벨', width: 70 },
  { field: 'designer', headerName: '디자이너', width: 90 },
  { field: 'vendor', headerName: '협력사', width: 200 },
  { field: 'graphic', headerName: '그래픽 코드', width: 160 },
  { field: 'color', headerName: '컬러', width: 250 },
];


const style = {
  width: '100%',
  fontSize: '0.8rem',
  '& .MuiDataGrid-columnHeader': {
    backgroundColor: '#F9F9FB !important',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 'bold',
  },
  '& .Mui-selected': {
    backgroundColor: 'rgba(0, 0, 0, 0.03) !important',
  },
  '& .MuiCheckbox-root': {
    color: '#666666',
    '&.Mui-checked': {
      color: '#666666',
    }
  },
  '& .MuiDataGrid-row': {
    cursor: 'pointer'
  },
  '& .MuiDataGrid-cell:focus': {
    outline: 'none'
  },
  '& .MuiDataGrid-cell:focus-within': {
    outline: 'none'
  },
  '& .MuiDataGrid-columnHeader:focus': {
    outline: 'none'
  },
  '& .MuiDataGrid-columnHeader:focus-within': {
    outline: 'none'
  }
};

const createOptions = (data, key, filterFn = (value) => value !== null) => {
  const options = Array.from(
    new Set(
      data.map((item) => (item[key] === null ? '-' : item[key])).filter(filterFn)
    )
  ).map((value) => ({
    value: key === 'life_style' ? value.toUpperCase() : value,
    label: key === 'life_style' ? value.toUpperCase() : value
  }));

  // 중복 제거 (distinct)
  const distinctOptions = Array.from(
    new Map(options.map((item) => [item.value, item])).values()
  );

  return distinctOptions.sort((a, b) => {
    // sesn : 숫자 내림차순, 영문 오름차순 정렬
    if (key === 'sesn') {
      // 'RUN'을 맨 먼저 표시
      if (a.value === 'RUN') return -1;
      if (b.value === 'RUN') return 1;

      const numA = parseInt(a.value, 10);
      const numB = parseInt(b.value, 10);

      // 둘 다 숫자가 아닌 경우 (RUN 제외)
      if (isNaN(numA) && isNaN(numB)) {
        return a.value.localeCompare(b.value);
      }
      // a만 숫자가 아닌 경우 (뒤로 보냄)
      if (isNaN(numA)) return 1;
      // b만 숫자가 아닌 경우 (뒤로 보냄)
      if (isNaN(numB)) return -1;

      // 둘 다 숫자인 경우
      const alphaA = a.value.slice(-2); // 'F', 'S' 등
      const alphaB = b.value.slice(-2);

      if (numA !== numB) {
        return numB - numA; // 숫자 내림차순
      }
      return alphaA.localeCompare(alphaB); // 영문 오름차순
    } else {
      // 기본 정렬 (예: '-'가 가장 먼저, 나머지는 알파벳 순)
      if (a.value === '-') return -1;
      if (b.value === '-') return 1;
      return a.value.localeCompare(b.value);
    }
  });
};


// Grid 
const Grid = ({ tableData, setSelectedRow, language }) => {
  const itemsPerPage = 100; // 한 페이지에 100개씩 보여줄 것
  const [currentPage, setCurrentPage] = useState(1); // 현재 페이지 상태

  // tableData가 변경될 때마다 페이지를 1로 리셋
  useEffect(() => {
    setCurrentPage(1);
  }, [tableData]);

  // 데이터의 시작 인덱스와 끝 인덱스를 계산하여 해당 페이지에 보여줄 데이터 추출
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = tableData.slice(indexOfFirstItem, indexOfLastItem);

  // 페이지 변경 시 호출되는 함수
  const handlePageChange = (event, value) => {
    setCurrentPage(value); // 페이지 변경 시 currentPage 상태 업데이트
  };

  const gridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
    gap: "20px",
    padding: "20px",
  };

  const cardStyle = {
    border: "1px solid #ddd",
    borderRadius: "5px",
    padding: "10px",
    textAlign: "center",
    boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
    cursor: "pointer",
    fontSize: "11px",
  };

  const imageStyle = {
    width: "100%",
    height: "100px",
    objectFit: 'contain',
    borderRadius: "4px",
  };

  return (
    <div>
      <div style={gridStyle}>
        {currentItems.map((item, index) => (
          <div
            key={index}
            style={cardStyle}
            onClick={() => setSelectedRow(item)} // 클릭된 데이터를 부모로 전달
          >
            <FlexBoxVertical>
              <FlexBoxHorizontal style={{ justifyContent: "space-between" }}>
                <div style={{ fontWeight: 'bold' }}>{item.part_cd !== null ? item.part_cd : ' '}</div>
                <div>{item.fabric !== null ? item.fabric : ' '}</div>
              </FlexBoxHorizontal>
              <FlexBoxHorizontal style={{ justifyContent: "center" }}>
                {language === 'ko' ? <div>{item.description !== null ? item.description : ' '}</div>
                  : <div>{item.description_eng !== null ? item.description_eng : ' '}</div>}
              </FlexBoxHorizontal>
              <FlexBoxHorizontal style={{ justifyContent: "space-between" }}>
                <div>{item.domain !== null ? item.domain : ' '}</div>
                <div>{item.set_up_style_no !== null ? item.set_up_style_no : ' '}</div>
                <div>{item.fit !== null ? item.fit : ' '}</div>
                <div>{item.tag !== null ? item.tag.toLocaleString() : ' '}</div>
              </FlexBoxHorizontal>
              <FlexBoxHorizontal style={{ justifyContent: "space-between" }}>
                <div>{item.life_style !== null ? item.life_style : ' '}</div>
                <div>{item.level !== null ? item.level : ' '}</div>
              </FlexBoxHorizontal>
              <img
                src={item.img !== null ? item.img : ' '} // 이미지 URL
                alt={`Style ${item.part_cd}`}
                onError={(e) => {
                  if (!e.target.dataset.fallback) {
                    e.target.dataset.fallback = '1';
                    e.target.src = `https://static-dashff.fnf.co.kr/interstellar/cad_img/${item.brd_cd}/${item.sesn}/${item.part_cd}.jpg?timestamp=${Date.now()}`;
                  } else {
                    e.target.src = 'https://img.icons8.com/?size=100&id=IuQpbmYpl7VN&format=png&color=dadee0';
                  }
                }}
                style={imageStyle}
              />
              <FlexBoxHorizontal style={{ justifyContent: "space-between" }}>
                <div>{item.designer !== null ? item.designer : ' '}</div>
                <div>{item.vendor !== null ? item.vendor : ' '}</div>
              </FlexBoxHorizontal>
              <FlexBoxHorizontal style={{ justifyContent: "center" }}>
                <div>{item.color !== null ? item.color : ' '}</div>
              </FlexBoxHorizontal>
            </FlexBoxVertical>
          </div>
        ))}
      </div>

      {/* 페이지네이션 추가 */}
      <Stack spacing={2} style={{ alignItems: 'center', margin: '0 0 20px 0' }}>
        <Pagination
          count={Math.ceil(tableData.length / itemsPerPage)} // 전체 페이지 수 계산
          page={currentPage} // 현재 페이지
          onChange={handlePageChange} // 페이지 변경 시 실행되는 함수
        />
      </Stack>
    </div>
  );
};